import { EntityTypes } from 'constants/entities';
import { Mode } from 'core/slices/session';
import { RootState } from 'types';

/**
 * The language to load a module or slide in, for requests that read one.
 *
 * Only the editor reads in a language: learners get a module from its published
 * file, and a learner-mode read never asks for a translation. The API refuses
 * any create, clone or non-text save that carries a language, so this is for
 * reads — a translation is saved as its own entity (TNL, see useSaveText).
 */
export function editLanguage(state: RootState, type?: EntityTypes): number | undefined {
  if (state.session?.mode !== Mode.ADMIN) return undefined;
  if (type && type !== EntityTypes.MODULE && type !== EntityTypes.ACTION) return undefined;

  return state.translation?.languageId ?? undefined;
}

/** The base language's code in learner links: `/en/{token}` is the module as written. */
export const BASE_LINK_LANGUAGE = 'en';

/**
 * A learner link's language segment, as an ISO code ("fr", "zh-Hans"), or
 * null when it isn't one. Case-folded for the language, so "FR" and "fr" name
 * the same file.
 */
export function linkLanguage(segment: string | undefined): string | null {
  if (!segment || !/^[a-z]{2,3}(-[a-z0-9]{2,8})?$/i.test(segment)) return null;

  const [language, region] = segment.split('-');

  return region ? `${language.toLowerCase()}-${region}` : language.toLowerCase();
}

/**
 * The language a learner-side module read asks for: the link's, unless it is
 * the base language. The editor never uses it — it has its own (`editLanguage`).
 */
export function learnerLanguage(state: RootState): string | undefined {
  if (state.session?.mode === Mode.ADMIN) return undefined;

  const language = state.translation?.learnerLanguage;

  return language && language !== BASE_LINK_LANGUAGE ? language : undefined;
}

/**
 * A translation is published beside its module's file, named for its language
 * (`module/{id}/{date}.fr.json` next to `{date}.json`), so the player can ask
 * for it without first reading the module's language list.
 */
export function translatedModuleUrl(url: string, language: string) {
  return /\.json(\?|$)/.test(url) ? url.replace(/\.json(\?|$)/, `.${language}.json$1`) : null;
}
